/**
 * Sync engine — orchestrates three triggers that flush the write outbox
 * and refresh the read cache:
 *
 *   1. browser `online` event
 *   2. Capacitor `appStateChange` (app comes to foreground)
 *   3. Periodic interval (every 15 min) as a JS fallback when background-runner
 *      is not available (web / emulator).
 *
 * Call `startSyncEngine()` once at app startup (inside App.tsx useEffect).
 * Call `stopSyncEngine()` on unmount (tests / hot-reload).
 *
 * The engine is intentionally lightweight: it calls flushOutbox() then
 * invalidates per-entity sync timestamps so the next readCache() call
 * re-fetches from the API.
 */

import { flushOutbox } from './outbox'
import { offlineDB } from './db'

const BACKGROUND_INTERVAL_MS = 15 * 60 * 1000   // 15 minutes

let _intervalId: ReturnType<typeof setInterval> | null = null
let _running = false

// ── Core sync routine ─────────────────────────────────────────────────────────

export async function runSync(): Promise<void> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return

  try {
    await flushOutbox()
    // Invalidate cached entity timestamps so the next hook read re-fetches
    await offlineDB.syncMeta.bulkPut([
      { entity: 'leads',       lastSyncedAt: new Date().toISOString() },
      { entity: 'customers',   lastSyncedAt: new Date().toISOString() },
      { entity: 'callHistory', lastSyncedAt: new Date().toISOString() },
    ])
  } catch (err) {
    // Best-effort — log and continue
    console.warn('[SyncEngine] sync failed', err)
  }
}

// ── Trigger: browser online event ─────────────────────────────────────────────

function _onOnline() {
  runSync()
}

// ── Trigger: Capacitor app foreground ─────────────────────────────────────────

let _capacitorUnsub: (() => void) | null = null

async function _attachCapacitorForeground() {
  try {
    const { App } = await import('@capacitor/app')
    const handle = await App.addListener('appStateChange', (state) => {
      if (state.isActive) runSync()
    })
    _capacitorUnsub = () => handle.remove()
  } catch {
    // Running in web mode — Capacitor not available, skip
  }
}

// ── Start / stop ──────────────────────────────────────────────────────────────

export async function startSyncEngine(): Promise<void> {
  if (_running) return
  _running = true

  window.addEventListener('online', _onOnline)
  await _attachCapacitorForeground()

  // Periodic fallback (JS interval)
  _intervalId = setInterval(runSync, BACKGROUND_INTERVAL_MS)

  // Run once immediately to drain any outbox entries from a previous session
  runSync().catch(err => console.warn('[SyncEngine] initial sync failed', err))
}

export function stopSyncEngine(): void {
  _running = false
  window.removeEventListener('online', _onOnline)
  if (_capacitorUnsub) { _capacitorUnsub(); _capacitorUnsub = null }
  if (_intervalId !== null) { clearInterval(_intervalId); _intervalId = null }
}
